import { X } from 'lucide-react';
import { type KeyboardEvent, useId, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { Tag } from './badge';

const fold = (v: string) => v.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();

/**
 * Tags livres em chips. Enter, vírgula ou Tab confirmam; Backspace no campo vazio remove a última.
 * Sugestões (tags já usadas) aparecem conforme a digitação e reaproveitam a grafia existente.
 */
export function TagInput({
  value,
  onChange,
  suggestions = [],
  placeholder = 'Adicionar tag',
  max,
  maxLength,
  id,
  className,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  max?: number;
  maxLength?: number;
  id?: string;
  className?: string;
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  // -1: nada destacado, Enter cria a tag digitada; setas escolhem uma sugestão.
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const full = max !== undefined && value.length >= max;

  const taken = useMemo(() => new Set(value.map(fold)), [value]);
  const options = useMemo(() => {
    const q = fold(draft);
    return suggestions
      .filter((s) => !taken.has(fold(s)) && (!q || fold(s).includes(q)))
      .slice(0, 8);
  }, [draft, suggestions, taken]);
  const showList = open && !full && options.length > 0;

  const add = (raw: string) => {
    const tag = raw.replace(/\s+/g, ' ').trim().slice(0, maxLength);
    setDraft('');
    setActive(-1);
    if (!tag || taken.has(fold(tag)) || full) return;
    const known = suggestions.find((s) => fold(s) === fold(tag));
    onChange([...value, known ?? tag]);
  };
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && showList) {
      e.preventDefault();
      setActive((a) => (a + 1) % options.length);
    } else if (e.key === 'ArrowUp' && showList) {
      e.preventDefault();
      setActive((a) => (a <= 0 ? options.length - 1 : a - 1));
    } else if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      const picked = showList && e.key !== ',' ? options[active] : undefined;
      const chosen = picked ?? draft;
      if (!chosen.trim()) return; // Tab sem nada digitado segue para o próximo campo
      e.preventDefault();
      add(chosen);
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      remove(value.length - 1);
    } else if (e.key === 'Escape' && showList) {
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <div
        onClick={() => inputRef.current?.focus()}
        className={cn(
          'flex min-h-control flex-wrap items-center gap-1 rounded border border-border-strong bg-surface-1 px-1.5 py-1 transition-[border-color,box-shadow] duration-(--dur-fast) ease-app focus-within:border-accent focus-within:shadow-glow',
          className,
        )}
      >
        {value.map((t, i) => (
          <Tag key={t} className="gap-0.5 pr-0.5">
            {t}
            <button
              type="button"
              aria-label={`Remover tag ${t}`}
              onClick={(e) => {
                e.stopPropagation();
                remove(i);
              }}
              className="inline-flex size-4 items-center justify-center rounded-sm text-text-3 hover:bg-surface-3 hover:text-text-1"
            >
              <X className="size-3" />
            </button>
          </Tag>
        ))}
        <input
          ref={inputRef}
          id={id}
          value={draft}
          disabled={full}
          maxLength={maxLength}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder={full ? `Limite de ${max} tags` : value.length ? '' : placeholder}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            if (draft.trim()) add(draft);
          }}
          onKeyDown={onKeyDown}
          className="h-6 min-w-[120px] flex-1 bg-transparent px-1 text-base text-text-1 outline-none placeholder:text-text-3 disabled:cursor-not-allowed"
        />
      </div>
      {showList ? (
        <ul
          id={listId}
          role="listbox"
          className="glass absolute left-0 right-0 top-full z-(--z-popover) mt-1 max-h-60 overflow-auto rounded p-1 shadow-popover"
        >
          {options.map((o, i) => (
            <li
              key={o}
              role="option"
              aria-selected={i === active}
              // mousedown: escolhe antes do blur do campo fechar a lista.
              onMouseDown={(e) => {
                e.preventDefault();
                add(o);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                'cursor-pointer rounded-sm px-2 py-1.5 text-sm text-text-2',
                i === active && 'bg-surface-3 text-text-1',
              )}
            >
              {o}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
